import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Calendar, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import VoiceRecorder from './VoiceRecorder';
import API_BASE_URL from '../apiConfig';

// ─────────────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────────────

// Returns today's date as YYYY-MM-DD in local timezone — matches AddVisitModal pattern.
const getLocalToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// 30-minute time slot generator (06:00–23:00), same as AddVisitModal.
const TIME_SLOTS = Array.from({ length: 34 }, (_, i) => {
    const totalMins = 360 + i * 30;
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    const period = h < 12 ? 'AM' : 'PM';
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return { value: `${hh}:${mm}`, label: `${h12}:${mm} ${period}` };
});

// Inject spinner keyframe once — reuses AddVisitModal's id guard pattern.
if (typeof document !== 'undefined' && !document.getElementById('overdue-modal-styles')) {
    const style = document.createElement('style');
    style.id = 'overdue-modal-styles';
    style.textContent = '@keyframes overdue-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared style tokens — inline-style pattern matches AddVisitModal.js exactly.
// ─────────────────────────────────────────────────────────────────────────────
const S = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 9100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        // overflow-y so tall modals (Scenario 2b step 2) don't clip on small screens
        overflowY: 'auto',
    },
    card: {
        background: 'rgb(var(--c-gray-800))',
        borderRadius: '16px',
        padding: '24px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        position: 'relative',
        // Ensure card doesn't exceed viewport height on small screens
        maxHeight: 'calc(var(--app-height) - 32px)',
        overflowY: 'auto',
    },
    closeBtn: {
        position: 'absolute',
        top: '16px',
        right: '16px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'rgb(var(--c-gray-500))',
        padding: '4px',
        borderRadius: '6px',
        width: '44px',
        height: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    header: {
        marginBottom: '20px',
        paddingRight: '36px',
    },
    title: {
        fontSize: '1.05rem',
        fontWeight: '700',
        color: 'rgb(var(--c-gray-100))',
        margin: '0 0 4px 0',
    },
    subtitle: {
        fontSize: '0.875rem',
        color: 'rgb(var(--c-gray-400))',
        margin: 0,
    },
    fieldGroup: {
        marginBottom: '16px',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.8125rem',
        fontWeight: '600',
        color: 'rgb(var(--c-gray-300))',
        marginBottom: '8px',
    },
    textarea: {
        width: '100%',
        padding: '10px 12px',
        border: '1.5px solid rgb(var(--c-gray-600))',
        borderRadius: '10px',
        fontSize: '0.875rem',
        color: 'rgb(var(--c-gray-100))',
        background: 'rgb(var(--c-gray-900))',
        boxSizing: 'border-box',
        outline: 'none',
        resize: 'vertical',
        minHeight: '90px',
        lineHeight: '1.5',
        fontFamily: 'inherit',
    },
    dateInput: {
        width: '100%',
        padding: '10px 12px',
        border: '1.5px solid rgb(var(--c-gray-600))',
        borderRadius: '10px',
        fontSize: '0.9375rem',
        color: 'rgb(var(--c-gray-100))',
        background: 'rgb(var(--c-gray-900))',
        boxSizing: 'border-box',
        outline: 'none',
        cursor: 'pointer',
        minHeight: '44px',
        maxWidth: '100%',
        // Prevent iOS Safari from overriding width with native date picker chrome
        appearance: 'none',
        WebkitAppearance: 'none',
    },
    radioGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '16px',
    },
    radioOption: (selected) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px',
        background: selected ? '#eff6ff' : '#f9fafb',
        borderRadius: '10px',
        border: `1.5px solid ${selected ? '#bfdbfe' : '#e5e7eb'}`,
        cursor: 'pointer',
        minHeight: '44px',
        transition: 'all 0.1s ease',
    }),
    radioInput: {
        width: '18px',
        height: '18px',
        accentColor: '#3b82f6',
        cursor: 'pointer',
        flexShrink: 0,
    },
    radioLabel: {
        fontSize: '0.875rem',
        fontWeight: '500',
        color: 'rgb(var(--c-blue-300))',
        cursor: 'pointer',
        userSelect: 'none',
    },
    checkboxRow: (checked) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px',
        background: checked ? '#eff6ff' : '#f9fafb',
        borderRadius: '10px',
        border: `1.5px solid ${checked ? '#bfdbfe' : '#e5e7eb'}`,
        cursor: 'pointer',
        minHeight: '44px',
        marginBottom: '12px',
    }),
    checkbox: {
        width: '18px',
        height: '18px',
        accentColor: '#3b82f6',
        cursor: 'pointer',
        flexShrink: 0,
    },
    checkboxLabel: {
        fontSize: '0.875rem',
        fontWeight: '500',
        color: 'rgb(var(--c-gray-300))',
        cursor: 'pointer',
        userSelect: 'none',
    },
    amberWarning: {
        background: 'rgb(var(--c-amber-900) / 0.3)',
        border: '1px solid rgb(var(--c-amber-700))',
        borderRadius: '8px',
        padding: '10px 12px',
        fontSize: '0.8125rem',
        color: 'rgb(var(--c-amber-300))',
        marginBottom: '14px',
        lineHeight: '1.5',
    },
    footer: {
        display: 'flex',
        gap: '10px',
        marginTop: '8px',
    },
    cancelBtn: {
        flex: 1,
        padding: '12px',
        border: '1.5px solid rgb(var(--c-gray-600))',
        borderRadius: '10px',
        background: 'rgb(var(--c-gray-800))',
        color: 'rgb(var(--c-gray-300))',
        fontWeight: '600',
        fontSize: '0.875rem',
        cursor: 'pointer',
        minHeight: '44px',
    },
    backBtn: {
        flex: 1,
        padding: '12px',
        border: '1.5px solid rgb(var(--c-gray-600))',
        borderRadius: '10px',
        background: 'rgb(var(--c-gray-800))',
        color: 'rgb(var(--c-gray-300))',
        fontWeight: '600',
        fontSize: '0.875rem',
        cursor: 'pointer',
        minHeight: '44px',
    },
    greenBtn: (disabled) => ({
        flex: 2,
        padding: '12px',
        border: 'none',
        borderRadius: '10px',
        background: disabled ? '#d1fae5' : '#16a34a',
        color: disabled ? '#6b7280' : '#ffffff',
        fontWeight: '600',
        fontSize: '0.875rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        opacity: disabled ? 0.6 : 1,
        transition: 'opacity 0.15s ease',
    }),
    orangeBtn: (disabled) => ({
        flex: 2,
        padding: '12px',
        border: 'none',
        borderRadius: '10px',
        background: disabled ? '#fed7aa' : '#ea580c',
        color: disabled ? '#6b7280' : '#ffffff',
        fontWeight: '600',
        fontSize: '0.875rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        opacity: disabled ? 0.6 : 1,
        transition: 'opacity 0.15s ease',
    }),
    blueBtn: (disabled) => ({
        flex: 2,
        padding: '12px',
        border: 'none',
        borderRadius: '10px',
        background: disabled ? '#bfdbfe' : '#2563eb',
        color: disabled ? '#6b7280' : '#ffffff',
        fontWeight: '600',
        fontSize: '0.875rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        opacity: disabled ? 0.6 : 1,
        transition: 'opacity 0.15s ease',
    }),
    spinner: {
        width: '16px',
        height: '16px',
        border: '2px solid rgba(255,255,255,0.4)',
        borderTopColor: '#ffffff',
        borderRadius: '50%',
        animation: 'overdue-spin 0.7s linear infinite',
        flexShrink: 0,
    },
    charCounter: (met) => ({
        fontSize: '0.75rem',
        color: met ? '#16a34a' : '#9ca3af',
        textAlign: 'right',
        marginTop: '4px',
        fontWeight: met ? 600 : 400,
    }),
    divider: {
        height: '1px',
        background: 'rgb(var(--c-gray-700))',
        margin: '16px 0',
    },
    timeRow: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
    },
    timeSelect: {
        flex: 1,
        padding: '10px 12px',
        border: '1.5px solid rgb(var(--c-gray-600))',
        borderRadius: '10px',
        fontSize: '0.875rem',
        color: 'rgb(var(--c-gray-100))',
        background: 'rgb(var(--c-gray-900))',
        boxSizing: 'border-box',
        outline: 'none',
        minHeight: '44px',
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// Scenario 1 — Visit Happened, Not Recorded
// Fields: overdue_reason (250-char minimum) + optional note/voice
// API: POST /api/visits/{id}/overdue-complete
// ─────────────────────────────────────────────────────────────────────────────
function ScenarioComplete({ visit, onClose, onSuccess }) {
    const [overdueReason, setOverdueReason] = useState('');
    const [completionNote, setCompletionNote] = useState('');
    const [voiceMemoUrl, setVoiceMemoUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const MIN_CHARS = 250;
    const charCount = overdueReason.length;
    const charsMet = charCount >= MIN_CHARS;
    const canSubmit = charsMet && !loading;

    const handleVoiceUpload = (data) => {
        if (data?.voice_memo_url) setVoiceMemoUrl(data.voice_memo_url);
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setLoading(true);
        setError('');
        try {
            await axios.post(`${API_BASE_URL}/api/visits/${visit.id}/overdue-complete`, {
                overdue_reason: overdueReason,
                notes: completionNote || undefined,
                voice_memo_url: voiceMemoUrl || undefined,
            });
            onSuccess({ refresh: false });
        } catch (err) {
            console.error('overdue-complete failed:', err);
            setError('Failed to save. Please try again.');
            setLoading(false);
        }
    };

    return (
        <>
            <div style={S.header}>
                <p style={S.title}>Mark as Completed</p>
                <p style={S.subtitle}>{visit?.entity_name}</p>
            </div>

            {/* Overdue explanation — 250-char minimum */}
            <div style={S.fieldGroup}>
                <label style={S.label} htmlFor="overdue-reason">
                    <AlertCircle size={14} color="#dc2626" />
                    Overdue Explanation <span style={{ color: 'rgb(var(--c-red-400))', marginLeft: '2px' }}>*</span>
                </label>
                <p style={{ fontSize: '0.75rem', color: 'rgb(var(--c-gray-400))', marginBottom: '8px', marginTop: 0 }}>
                    Why wasn't this visit recorded at the time it happened?
                </p>
                <textarea
                    id="overdue-reason"
                    value={overdueReason}
                    onChange={(e) => setOverdueReason(e.target.value)}
                    placeholder="Describe what happened and why this visit wasn't recorded when it occurred..."
                    style={S.textarea}
                    disabled={loading}
                />
                <div style={S.charCounter(charsMet)}>
                    {charCount} / {MIN_CHARS} min {charsMet && '✓'}
                </div>
            </div>

            <div style={S.divider} />

            {/* Completion note (optional) — same pattern as Today's Visits */}
            <div style={S.fieldGroup}>
                <label style={S.label}>
                    <CheckCircle size={14} />
                    Completion Note <span style={{ color: 'rgb(var(--c-gray-500))', fontWeight: 400, marginLeft: '4px' }}>(optional)</span>
                </label>
                <textarea
                    value={completionNote}
                    onChange={(e) => setCompletionNote(e.target.value)}
                    placeholder="How did the visit go? Any follow-ups?"
                    style={{ ...S.textarea, minHeight: '70px' }}
                    disabled={loading}
                />
            </div>

            {/* Voice recorder — same component used in TodaysVisits */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgb(var(--c-gray-400))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Record Memo
                </span>
                <VoiceRecorder
                    customerId={visit?.entity_type === 'customer' ? parseInt(visit.entity_global_id.split('_')[1]) : undefined}
                    leadId={visit?.entity_type === 'lead' ? parseInt(visit.entity_global_id.split('_')[1]) : undefined}
                    visitId={visit?.id}
                    onUploadSuccess={handleVoiceUpload}
                />
            </div>

            {error && (
                <p style={{ color: 'rgb(var(--c-red-400))', fontSize: '0.8125rem', marginBottom: '12px' }}>{error}</p>
            )}

            <div style={S.footer}>
                <button style={S.cancelBtn} onClick={onClose} disabled={loading}>Cancel</button>
                <button
                    style={S.greenBtn(!canSubmit)}
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    aria-busy={loading}
                >
                    {loading ? (
                        <><span style={S.spinner} aria-hidden="true" /> Saving...</>
                    ) : (
                        <><CheckCircle size={15} /> Mark Completed</>
                    )}
                </button>
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Scenario 2a — Ad-hoc Visit Didn't Happen (customer_confirmed=false)
// Fields: reason + reschedule/cancel choice + optional date/time
// API: POST /api/visits/{id}/overdue-resolve
// ─────────────────────────────────────────────────────────────────────────────
function ScenarioResolveAdhoc({ visit, onClose, onSuccess }) {
    const [reason, setReason] = useState('');
    const [action, setAction] = useState(''); // 'reschedule' | 'cancel'
    const [newDate, setNewDate] = useState('');
    const [newTime, setNewTime] = useState('');
    const [newEndTime, setNewEndTime] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const endSlots = newTime ? TIME_SLOTS.filter(s => s.value > newTime) : TIME_SLOTS;

    const handleStartTimeChange = (e) => {
        const v = e.target.value;
        setNewTime(v);
        if (newEndTime && newEndTime <= v) setNewEndTime('');
    };

    const canSubmit = reason.trim() && action && (action === 'cancel' || newDate) && !loading;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setLoading(true);
        setError('');
        try {
            await axios.post(`${API_BASE_URL}/api/visits/${visit.id}/overdue-resolve`, {
                action,
                reason,
                new_date: action === 'reschedule' ? newDate : undefined,
                new_time: action === 'reschedule' && newTime ? newTime : undefined,
                new_end_time: action === 'reschedule' && newTime && newEndTime ? newEndTime : undefined,
            });
            onSuccess({ refresh: action === 'reschedule' });
        } catch (err) {
            console.error('overdue-resolve failed:', err);
            setError('Failed to save. Please try again.');
            setLoading(false);
        }
    };

    return (
        <>
            <div style={S.header}>
                <p style={S.title}>Didn't Happen</p>
                <p style={S.subtitle}>{visit?.entity_name}</p>
            </div>

            {/* What happened */}
            <div style={S.fieldGroup}>
                <label style={S.label} htmlFor="adhoc-reason">
                    What happened? <span style={{ color: 'rgb(var(--c-red-400))', marginLeft: '2px' }}>*</span>
                </label>
                <textarea
                    id="adhoc-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Briefly explain why this visit didn't happen..."
                    style={S.textarea}
                    disabled={loading}
                />
            </div>

            {/* Action choice */}
            <div style={{ ...S.fieldGroup, marginBottom: '0' }}>
                <label style={S.label}>What would you like to do?</label>
                <div style={S.radioGroup}>
                    {[
                        { value: 'reschedule', label: 'Reschedule for another day' },
                        { value: 'cancel', label: 'Cancel this visit' },
                    ].map(opt => (
                        <div
                            key={opt.value}
                            style={S.radioOption(action === opt.value)}
                            onClick={() => !loading && setAction(opt.value)}
                        >
                            <input
                                type="radio"
                                name="adhoc-action"
                                value={opt.value}
                                checked={action === opt.value}
                                onChange={() => setAction(opt.value)}
                                style={S.radioInput}
                                disabled={loading}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span style={S.radioLabel}>{opt.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Date/time picker — shown when reschedule is selected */}
            {action === 'reschedule' && (
                <div style={{ ...S.fieldGroup, marginTop: '12px' }}>
                    <label style={S.label} htmlFor="adhoc-date">
                        <Calendar size={14} />
                        New Date <span style={{ color: 'rgb(var(--c-red-400))', marginLeft: '2px' }}>*</span>
                    </label>
                    <input
                        id="adhoc-date"
                        type="date"
                        value={newDate}
                        min={getLocalToday()}
                        onChange={(e) => setNewDate(e.target.value)}
                        style={S.dateInput}
                        disabled={loading}
                    />

                    <label style={{ ...S.label, marginTop: '12px' }}>
                        <Clock size={14} />
                        Time <span style={{ color: 'rgb(var(--c-gray-500))', fontWeight: 400, marginLeft: '4px' }}>(optional)</span>
                    </label>
                    <div style={S.timeRow}>
                        <select
                            value={newTime}
                            onChange={handleStartTimeChange}
                            style={S.timeSelect}
                            disabled={loading}
                        >
                            <option value="">Start</option>
                            {TIME_SLOTS.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                        <span style={{ color: 'rgb(var(--c-gray-500))', fontSize: '0.8125rem', flexShrink: 0 }}>to</span>
                        <select
                            value={newEndTime}
                            onChange={(e) => setNewEndTime(e.target.value)}
                            style={{ ...S.timeSelect, opacity: newTime ? 1 : 0.4 }}
                            disabled={loading || !newTime}
                        >
                            <option value="">End</option>
                            {endSlots.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {error && (
                <p style={{ color: 'rgb(var(--c-red-400))', fontSize: '0.8125rem', margin: '8px 0 0' }}>{error}</p>
            )}

            <div style={{ ...S.footer, marginTop: '16px' }}>
                <button style={S.cancelBtn} onClick={onClose} disabled={loading}>Cancel</button>
                <button
                    style={S.orangeBtn(!canSubmit)}
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    aria-busy={loading}
                >
                    {loading ? (
                        <><span style={S.spinner} aria-hidden="true" /> Saving...</>
                    ) : (
                        action === 'reschedule' ? 'Reschedule' : action === 'cancel' ? 'Cancel Visit' : 'Confirm'
                    )}
                </button>
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Scenario 2b — Scheduled Visit Didn't Happen (customer_confirmed=true)
// Two-step flow: Step 1 — reason + customer informed? + action choice
//                Step 2 — (if reschedule) date/time + customer confirmed toggle
// API: POST /api/visits/{id}/overdue-resolve
// ─────────────────────────────────────────────────────────────────────────────
function ScenarioResolveScheduled({ visit, onClose, onSuccess }) {
    const [step, setStep] = useState(1);
    const [reason, setReason] = useState('');
    const [customerInformed, setCustomerInformed] = useState(false);
    const [action, setAction] = useState(''); // 'reschedule' | 'cancel'

    // Step 2 fields
    const [newDate, setNewDate] = useState('');
    const [newTime, setNewTime] = useState('');
    const [newEndTime, setNewEndTime] = useState('');
    const [newCustomerConfirmed, setNewCustomerConfirmed] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const endSlots = newTime ? TIME_SLOTS.filter(s => s.value > newTime) : TIME_SLOTS;

    const handleStartTimeChange = (e) => {
        const v = e.target.value;
        setNewTime(v);
        if (newEndTime && newEndTime <= v) setNewEndTime('');
    };

    const step1CanProceed = reason.trim() && action;

    const handleStep1Next = () => {
        if (!step1CanProceed) return;
        if (action === 'cancel') {
            // Submit directly — no step 2 needed for cancel
            handleSubmit('cancel');
        } else {
            setStep(2);
        }
    };

    const handleSubmit = async (overrideAction) => {
        const resolvedAction = overrideAction || action;
        setLoading(true);
        setError('');
        try {
            await axios.post(`${API_BASE_URL}/api/visits/${visit.id}/overdue-resolve`, {
                action: resolvedAction,
                reason,
                customer_informed: customerInformed,
                new_date: resolvedAction === 'reschedule' ? newDate : undefined,
                new_time: resolvedAction === 'reschedule' && newTime ? newTime : undefined,
                new_end_time: resolvedAction === 'reschedule' && newTime && newEndTime ? newEndTime : undefined,
            });
            onSuccess({ refresh: resolvedAction === 'reschedule' });
        } catch (err) {
            console.error('overdue-resolve scheduled failed:', err);
            setError('Failed to save. Please try again.');
            setLoading(false);
        }
    };

    const step2CanSubmit = newDate && !loading;

    // ── Step 1 ──────────────────────────────────────────────────────────────
    if (step === 1) {
        return (
            <>
                <div style={S.header}>
                    <p style={S.title}>Didn't Happen</p>
                    <p style={S.subtitle}>{visit?.entity_name} — confirmed appointment</p>
                </div>

                <div style={S.fieldGroup}>
                    <label style={S.label} htmlFor="scheduled-reason">
                        What happened? <span style={{ color: 'rgb(var(--c-red-400))', marginLeft: '2px' }}>*</span>
                    </label>
                    <textarea
                        id="scheduled-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Explain why this confirmed appointment didn't happen..."
                        style={S.textarea}
                        disabled={loading}
                    />
                </div>

                {/* Customer informed checkbox */}
                <div
                    style={S.checkboxRow(customerInformed)}
                    onClick={() => !loading && setCustomerInformed(v => !v)}
                >
                    <input
                        type="checkbox"
                        checked={customerInformed}
                        onChange={(e) => setCustomerInformed(e.target.checked)}
                        style={S.checkbox}
                        disabled={loading}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <label
                        style={S.checkboxLabel}
                        onClick={(e) => e.stopPropagation()}
                    >
                        The customer was informed of the change
                    </label>
                </div>

                {/* Warning when customer NOT informed */}
                {!customerInformed && reason.length > 0 && (
                    <div style={S.amberWarning}>
                        The customer may still be expecting this visit. Make sure to reach out.
                    </div>
                )}

                {/* Action choice */}
                <div style={S.fieldGroup}>
                    <label style={S.label}>What would you like to do?</label>
                    <div style={S.radioGroup}>
                        {[
                            { value: 'reschedule', label: 'Schedule a New Visit' },
                            { value: 'cancel', label: 'Cancel Only' },
                        ].map(opt => (
                            <div
                                key={opt.value}
                                style={S.radioOption(action === opt.value)}
                                onClick={() => !loading && setAction(opt.value)}
                            >
                                <input
                                    type="radio"
                                    name="scheduled-action"
                                    value={opt.value}
                                    checked={action === opt.value}
                                    onChange={() => setAction(opt.value)}
                                    style={S.radioInput}
                                    disabled={loading}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <span style={S.radioLabel}>{opt.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {error && (
                    <p style={{ color: 'rgb(var(--c-red-400))', fontSize: '0.8125rem', marginBottom: '8px' }}>{error}</p>
                )}

                <div style={S.footer}>
                    <button style={S.cancelBtn} onClick={onClose} disabled={loading}>Cancel</button>
                    <button
                        style={S.orangeBtn(!step1CanProceed)}
                        onClick={handleStep1Next}
                        disabled={!step1CanProceed || loading}
                        aria-busy={loading}
                    >
                        {loading ? (
                            <><span style={S.spinner} aria-hidden="true" /> Saving...</>
                        ) : (
                            action === 'cancel' ? 'Cancel Visit' : action === 'reschedule' ? 'Next' : 'Continue'
                        )}
                    </button>
                </div>
            </>
        );
    }

    // ── Step 2 (reschedule only) ─────────────────────────────────────────────
    return (
        <>
            <div style={S.header}>
                <p style={S.title}>Schedule a New Visit</p>
                <p style={S.subtitle}>{visit?.entity_name}</p>
            </div>

            <div style={S.fieldGroup}>
                <label style={S.label} htmlFor="scheduled-new-date">
                    <Calendar size={14} />
                    New Date <span style={{ color: 'rgb(var(--c-red-400))', marginLeft: '2px' }}>*</span>
                </label>
                <input
                    id="scheduled-new-date"
                    type="date"
                    value={newDate}
                    min={getLocalToday()}
                    onChange={(e) => setNewDate(e.target.value)}
                    style={S.dateInput}
                    disabled={loading}
                />
            </div>

            <div style={S.fieldGroup}>
                <label style={S.label}>
                    <Clock size={14} />
                    Time <span style={{ color: 'rgb(var(--c-gray-500))', fontWeight: 400, marginLeft: '4px' }}>(optional)</span>
                </label>
                <div style={S.timeRow}>
                    <select
                        value={newTime}
                        onChange={handleStartTimeChange}
                        style={S.timeSelect}
                        disabled={loading}
                    >
                        <option value="">Start</option>
                        {TIME_SLOTS.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                    <span style={{ color: 'rgb(var(--c-gray-500))', fontSize: '0.8125rem', flexShrink: 0 }}>to</span>
                    <select
                        value={newEndTime}
                        onChange={(e) => setNewEndTime(e.target.value)}
                        style={{ ...S.timeSelect, opacity: newTime ? 1 : 0.4 }}
                        disabled={loading || !newTime}
                    >
                        <option value="">End</option>
                        {endSlots.map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Customer confirmed toggle — pre-checked, same as AddVisitModal */}
            <div
                style={S.checkboxRow(newCustomerConfirmed)}
                onClick={() => !loading && setNewCustomerConfirmed(v => !v)}
            >
                <input
                    type="checkbox"
                    checked={newCustomerConfirmed}
                    onChange={(e) => setNewCustomerConfirmed(e.target.checked)}
                    style={S.checkbox}
                    disabled={loading}
                    onClick={(e) => e.stopPropagation()}
                />
                <label style={S.checkboxLabel} onClick={(e) => e.stopPropagation()}>
                    Customer confirmed?
                </label>
            </div>

            {error && (
                <p style={{ color: 'rgb(var(--c-red-400))', fontSize: '0.8125rem', marginBottom: '8px' }}>{error}</p>
            )}

            <div style={S.footer}>
                <button style={S.backBtn} onClick={() => { setStep(1); setError(''); }} disabled={loading}>
                    Back
                </button>
                <button
                    style={S.blueBtn(!step2CanSubmit)}
                    onClick={() => handleSubmit('reschedule')}
                    disabled={!step2CanSubmit}
                    aria-busy={loading}
                >
                    {loading ? (
                        <><span style={S.spinner} aria-hidden="true" /> Saving...</>
                    ) : (
                        <><Calendar size={15} /> Schedule Visit</>
                    )}
                </button>
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// Scenario 2c — Bulk Absent Day
// Opened from the bulk-absent banner when 2+ visits share the same past date.
// API: POST /api/visits/bulk-absent
// ─────────────────────────────────────────────────────────────────────────────
function ScenarioBulkAbsent({ absentDate, absentVisitCount, onClose, onSuccess }) {
    const [action, setAction] = useState(''); // 'reschedule' | 'cancel'
    const [newDate, setNewDate] = useState('');
    const [customerInformed, setCustomerInformed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Format absent date for display
    const displayDate = absentDate
        ? new Date(absentDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : '';

    const canSubmit = action && (action === 'cancel' || newDate) && !loading;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setLoading(true);
        setError('');
        try {
            await axios.post(`${API_BASE_URL}/api/visits/bulk-absent`, {
                absent_date: absentDate,
                action,
                new_date: action === 'reschedule' ? newDate : undefined,
                customer_informed: customerInformed,
            });
            onSuccess({ refresh: true });
        } catch (err) {
            console.error('bulk-absent failed:', err);
            setError('Failed to save. Please try again.');
            setLoading(false);
        }
    };

    return (
        <>
            <div style={S.header}>
                <p style={S.title}>Handle All Visits from {displayDate}</p>
                <p style={S.subtitle}>
                    Resolve {absentVisitCount} overdue {absentVisitCount === 1 ? 'visit' : 'visits'} from this date
                </p>
            </div>

            {/* Summary context box */}
            <div style={{
                background: 'rgb(var(--c-amber-900) / 0.4)',
                border: '1px solid rgb(var(--c-amber-700))',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '0.8125rem',
                color: 'rgb(var(--c-amber-300))',
                marginBottom: '16px',
                lineHeight: '1.5',
            }}>
                You had {absentVisitCount} {absentVisitCount === 1 ? 'visit' : 'visits'} planned for {displayDate} that
                were never completed. Choose how to resolve them all at once.
            </div>

            {/* Action choice */}
            <div style={S.fieldGroup}>
                <label style={S.label}>How would you like to handle these visits?</label>
                <div style={S.radioGroup}>
                    {[
                        { value: 'reschedule', label: 'Reschedule All to a New Date' },
                        { value: 'cancel', label: 'Cancel All' },
                    ].map(opt => (
                        <div
                            key={opt.value}
                            style={S.radioOption(action === opt.value)}
                            onClick={() => !loading && setAction(opt.value)}
                        >
                            <input
                                type="radio"
                                name="bulk-action"
                                value={opt.value}
                                checked={action === opt.value}
                                onChange={() => setAction(opt.value)}
                                style={S.radioInput}
                                disabled={loading}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <span style={S.radioLabel}>{opt.label}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* New date picker — shown when reschedule selected */}
            {action === 'reschedule' && (
                <div style={S.fieldGroup}>
                    <label style={S.label} htmlFor="bulk-new-date">
                        <Calendar size={14} />
                        New Date for All Visits <span style={{ color: 'rgb(var(--c-red-400))', marginLeft: '2px' }}>*</span>
                    </label>
                    <input
                        id="bulk-new-date"
                        type="date"
                        value={newDate}
                        min={getLocalToday()}
                        onChange={(e) => setNewDate(e.target.value)}
                        style={S.dateInput}
                        disabled={loading}
                    />
                </div>
            )}

            {/* Customer informed — relevant when any visit had customer_confirmed=true */}
            <div
                style={S.checkboxRow(customerInformed)}
                onClick={() => !loading && setCustomerInformed(v => !v)}
            >
                <input
                    type="checkbox"
                    checked={customerInformed}
                    onChange={(e) => setCustomerInformed(e.target.checked)}
                    style={S.checkbox}
                    disabled={loading}
                    onClick={(e) => e.stopPropagation()}
                />
                <label style={S.checkboxLabel} onClick={(e) => e.stopPropagation()}>
                    Customers were informed of the change
                </label>
            </div>

            {/* Warning if not informed */}
            {!customerInformed && action && (
                <div style={S.amberWarning}>
                    Some of these visits may have had confirmed appointments. Make sure affected customers are notified.
                </div>
            )}

            {error && (
                <p style={{ color: 'rgb(var(--c-red-400))', fontSize: '0.8125rem', marginBottom: '8px' }}>{error}</p>
            )}

            <div style={S.footer}>
                <button style={S.cancelBtn} onClick={onClose} disabled={loading}>Cancel</button>
                <button
                    style={S.blueBtn(!canSubmit)}
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                    aria-busy={loading}
                >
                    {loading ? (
                        <><span style={S.spinner} aria-hidden="true" /> Saving...</>
                    ) : (
                        action === 'reschedule' ? 'Reschedule All' : action === 'cancel' ? 'Cancel All' : 'Confirm'
                    )}
                </button>
            </div>
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// OverdueResolutionModal — public entry point
//
// Props:
//   isOpen           bool
//   onClose          () => void
//   scenario         'complete' | 'resolve-adhoc' | 'resolve-scheduled' | 'bulk-absent'
//   visit            visit object (null for bulk-absent)
//   absentDate       YYYY-MM-DD string (bulk-absent only)
//   absentVisitCount integer (bulk-absent only)
//   onSuccess        ({ refresh: bool }) => void — called after successful API call
// ─────────────────────────────────────────────────────────────────────────────
export default function OverdueResolutionModal({
    isOpen,
    onClose,
    scenario,
    visit,
    absentDate,
    absentVisitCount,
    onSuccess,
}) {
    // Close on Escape key — consistent with AddVisitModal pattern
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const renderContent = () => {
        switch (scenario) {
            case 'complete':
                return <ScenarioComplete visit={visit} onClose={onClose} onSuccess={onSuccess} />;
            case 'resolve-adhoc':
                return <ScenarioResolveAdhoc visit={visit} onClose={onClose} onSuccess={onSuccess} />;
            case 'resolve-scheduled':
                return <ScenarioResolveScheduled visit={visit} onClose={onClose} onSuccess={onSuccess} />;
            case 'bulk-absent':
                return (
                    <ScenarioBulkAbsent
                        absentDate={absentDate}
                        absentVisitCount={absentVisitCount}
                        onClose={onClose}
                        onSuccess={onSuccess}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div
            style={S.backdrop}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            {/* Stop click propagation so backdrop handler doesn't fire from inside card */}
            <div style={S.card} onClick={(e) => e.stopPropagation()}>
                <button
                    style={S.closeBtn}
                    onClick={onClose}
                    aria-label="Close"
                >
                    <X size={18} />
                </button>
                {renderContent()}
            </div>
        </div>
    );
}
